import { useState, useEffect } from "react";

import { FormikProps } from "formik";
import { v4 as uuid } from "uuid";
import dayjs from "dayjs";

import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import InputGroup from "react-bootstrap/InputGroup";

import { TypeofAttendanceFilterInitialValues } from "@/utils/initialValues/attendanceFilter";
import { Option } from "@/@types/generics/Option";

import { FormattedControl } from "@/components/FormattedControl";
import { RedAsterisk } from "@/components/RedAsterisk";
import {
	ClinicOrSpecialtyOptionValue,
	ClinicOrSpecialtySelect,
} from "@/components/sintegrada/Selects/ClinicOrSpecialtySelect";
import { RiskClassificationSelect } from "@/components/sintegrada/Selects/RiskClassificationSelect";
import { GenderSelect } from "@/components/GenderSelect";
import { AgeGroup } from "@/components/sintegrada/AgeGroup";
import { ProfessionalSelect } from "@/components/sintegrada/Selects/ProfessionalSelect";
import { ApplyFilterButton } from "@/components/sintegrada/Buttons/ApplyFilterButton";
import { ExportExcelButton } from "@/components/sintegrada/Buttons/ExportExcelButton";

type Props = {
	formik: FormikProps<TypeofAttendanceFilterInitialValues>;
	disabledExportButton?: boolean;
	handleExportExcel: () => void;
};

export function MedicalAttendanceFilter({
	formik,
	disabledExportButton,
	handleExportExcel,
}: Readonly<Props>) {
	const { values, touched, errors } = formik;

	const [professionalSelectKey, setProfessionalSelectKey] = useState(uuid());

	const specialty = values.clinicOrSpecialty?.value?.specialty;
	const specialtyCbo = specialty?.cbo?.substring(0, 4);

	const minDate = dayjs().subtract(2, "years").startOf("day").format("YYYY-MM-DDTHH:mm");
	const maxDate = dayjs().endOf("day").format("YYYY-MM-DDTHH:mm");

	function handleChangeYoungerAge(value: string) {
		formik.setFieldValue("youngerAge", value);
	}

	function handleChangeOlderAge(value: string) {
		formik.setFieldValue("olderAge", value);
	}

	function handleChangeClinicOrSpecialty(option: Option<ClinicOrSpecialtyOptionValue>) {
		formik.setFieldValue("clinicOrSpecialty", option);
	}

	useEffect(() => {
		setProfessionalSelectKey(uuid());

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values.clinicOrSpecialty]);

	return (
		<Form className="mb-8" onSubmit={formik.handleSubmit}>
			<Col md={12} className="d-flex gap-4 mb-4">
				<div className="w-25">
					<Form.Label title="Data inicial" className="form-label" htmlFor="start-date">
						Data inicial <RedAsterisk />
					</Form.Label>

					<div className="d-flex justify-content-between gap-1">
						<InputGroup>
							<FormattedControl
								id="start-date"
								type="datetime-local"
								placeholder="Data inicial"
								min={minDate}
								max={maxDate}
								{...formik.getFieldProps("startDate")}
							/>

							{touched.startDate && errors.startDate && (
								<span className="text-danger">{errors.startDate}</span>
							)}
						</InputGroup>
						<InputGroup>
							<FormattedControl
								id="end-date"
								type="datetime-local"
								placeholder="Data final"
								min={minDate}
								max={maxDate}
								{...formik.getFieldProps("endDate")}
							/>

							{touched.endDate && errors.endDate && (
								<span className="text-danger">{errors.endDate}</span>
							)}
						</InputGroup>
					</div>
				</div>

				<div className="w-25">
					<Form.Label title="Setor" htmlFor="department">
						Setor <RedAsterisk />
					</Form.Label>
					
					<ClinicOrSpecialtySelect
						inputId="department"
						placeholder="Selecione..."
						noOptionsMessage={() => "Sem opções"}
						value={values.clinicOrSpecialty}
						handleChangeClinicOrSpecialty={handleChangeClinicOrSpecialty}
					/>

					{touched.clinicOrSpecialty?.label && errors.clinicOrSpecialty?.label && (
						<span className="text-danger">{errors.clinicOrSpecialty.label}</span>
					)}
				</div>

				<div className="w-25">
					<RiskClassificationSelect
						value={values.riskClassification}
						onChange={(option) => formik.setFieldValue("riskClassification", option)}
					/>
				</div>

				<div className="w-25">
					<GenderSelect
						labelClassNames="form-label"
						value={values.gender}
						onChange={(option) => formik.setFieldValue("gender", option)}
					/>
				</div>
			</Col>

			<Col md={12} className="d-flex gap-4 mb-4">
				<div className="w-25 d-flex gap-4">
					<div className="w-50">
						<GenderSelect
							labelClassNames="form-label"
							value={values.gender}
							onChange={(option) => formik.setFieldValue("gender", option)}
						/>
					</div>

					<div className="w-50">
						<AgeGroup
							minAge={values.youngerAge}
							maxAge={values.olderAge}
							handleChangeMinAge={handleChangeYoungerAge}
							handleChangeMaxAge={handleChangeOlderAge}
						/>
					</div>
				</div>

				<div className="w-25">
					<label title="Profissional" className="form-label" htmlFor="professional">
						Profissional
					</label>
					
					<ProfessionalSelect
						inputId="professional"
						key={professionalSelectKey}
						value={values.professional}
						cbos={[specialtyCbo]}
						onChange={(option) => formik.setFieldValue("professional", option)}
					/>
				</div>

				<div className="w-50 d-flex ms-4">
					<div className="w-25 d-flex align-items-end mx-8 mb-1">
						<Form.Check
							id="resume-by-professional"
							label="Individual"
							{...formik.getFieldProps("resumeByProfessional")}
						/>
					</div>

					<div className="d-flex align-items-end gap-4">
						<ApplyFilterButton type="submit" disabled={formik.isSubmitting || !formik.isValid} />
						
						<ExportExcelButton
							disabledExportButton={disabledExportButton}
							onClick={handleExportExcel}
						/>
					</div>
				</div>
			</Col>
		</Form>
	);
}
